import {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {setQAMetrics} from '@/store/onboardingSlices'
import {getAdoBoardName, getBuildTitle} from '@/components/SelfOnboarding/util/getLabel'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {QA_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {Feedback, Label} from 'pepsico-ds'
import {anyWidgetFieldTouched} from '@/components/SelfOnboarding/util/widgetErrorUtils'
import type {RootState} from '@/store'

type Suggestion = {id: string; label: string; fullObject: any}

const initialQAMetric = {adoBoard: '', buildPipeline: ''}
const QAMetricsWidget = ({onValidationChange}: {onValidationChange?: (hasError: boolean) => void}) => {
  const qaMetrics = useSelector((state: any) => (Array.isArray(state.qa) ? state.qa : [{...initialQAMetric}]))
  const dispatch = useDispatch()
  const [adoBoardSuggestions, setAdoBoardSuggestions] = useState<Suggestion[]>([])
  const [buildPipelineSuggestions, setBuildPipelineSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState<{[idx: number]: {adoBoard?: boolean; buildPipeline?: boolean}}>({})
  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')
  // Compute widgetTouched for field-level error display
  const widgetTouched = anyWidgetFieldTouched(touched)

  // Utility to check if adoBoard or buildPipeline is empty (handles string or object)
  const isFieldEmpty = (val: any) => {
    if (!val) return true
    if (typeof val === 'string') return val.trim() === ''
    if (typeof val === 'object') {
      return !val.label && !val.description
    }
    return false
  }
  type QAMetric = {
    adoBoard: string | {label?: string; description?: string}
    buildPipeline: string | {label?: string; description?: string}
  }

  const updateQAMetric = (idx: number, field: string, value: string) => {
    const newQAMetrics = qaMetrics.map((q: QAMetric, i: number) => (i === idx ? {...q, [field]: value} : q))
    dispatch(setQAMetrics(newQAMetrics))
  }

  // Widget-level error: only after touch
  useEffect(() => {
    const hasError = qaMetrics.some(
      (qa: QAMetric, idx: number) =>
        (touched[idx]?.adoBoard && isFieldEmpty(qa.adoBoard)) ||
        (touched[idx]?.buildPipeline && isFieldEmpty(qa.buildPipeline)),
    )
    if (onValidationChange) onValidationChange(hasError)
  }, [touched, qaMetrics, onValidationChange])

  const handleBlur = (idx: number, field: string) => {
    setTouched(prev => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {}),
        [field]: true,
      },
    }))
  }

  return (
    <>
      {qaMetrics.map((qa: any, idx: number) => (
        <CardWrapper
          key={idx}
          cardHeader={{
            rightAccessory: {
              iconButtons: [
                {
                  icon: 'delete_outline',
                  onClick: () => {
                    dispatch(setQAMetrics([{...initialQAMetric}]))
                    setTouched({})
                  },
                },
              ],
            },
          }}
          cardBody={{
            type: 'slot',
            slot: <></>,
          }}
          state="non-interactive"
          type="basic"
        >
          <>
            <form className="flex w-full flex-col items-start">
              <div className="flex w-full items-start gap-4">
                <div className="flex w-[282px] flex-col items-start">
                  <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                    <Label className={sharedStyles.autocompleteLabelFont} label={QA_WIDGET.label} />
                    <AutoCompleteWrapper
                      icon="keyboard_arrow_down"
                      limitSuggestions={100}
                      placeholderText={QA_WIDGET.placeholder}
                      onClear={() => updateQAMetric(idx, 'adoBoard', '')}
                      suggestions={adoBoardSuggestions}
                      isAutoComplete={true}
                      isLeadingIcon={false}
                      searchTerm={
                        typeof qa.adoBoard === 'string'
                          ? qa.adoBoard
                          : qa.adoBoard && typeof qa.adoBoard === 'object'
                            ? qa.adoBoard.label || ''
                            : ''
                      }
                      onUpdate={async (query: string) => {
                        try {
                          const {getItemsByTypeBySearchField} = await import('@/services/team/teamService')
                          const results = await getItemsByTypeBySearchField('AgileTool', {
                            search: query,
                            searchField: 'options.teamName',
                          })
                          const mapped = Array.isArray(results)
                            ? results.map((item: any) => ({
                                id: item.id || item._id || item.value || item.label,
                                label: getAdoBoardName(item),
                                fullObject: item,
                              }))
                            : []
                          setAdoBoardSuggestions(mapped)
                        } catch (err) {
                          // setError('Failed to load suggestions')
                        }
                      }}
                      onSelectOption={(option: any) => updateQAMetric(idx, 'adoBoard', option)}
                      onBlur={() => handleBlur(idx, 'adoBoard')}
                    />
                    <Feedback
                      variant="info"
                      className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                      message={QA_WIDGET.helperText}
                    />
                    {widgetTouched && isFieldEmpty(qa.adoBoard) && (
                      <div className="mt-1 text-xs text-red-500">{QA_WIDGET.error}</div>
                    )}
                  </div>
                  {error && (
                    <div className="error" style={{color: 'red', marginTop: 4}}>
                      {error}
                    </div>
                  )}
                </div>
                <div className="flex w-[282px] flex-col items-start">
                  <Label className={sharedStyles.autocompleteLabelFont} label={QA_WIDGET.buildPipelineLabel} />
                  <AutoCompleteWrapper
                    icon="keyboard_arrow_down"
                    limitSuggestions={100}
                    placeholderText={QA_WIDGET.placeholder}
                    onClear={() => updateQAMetric(idx, 'buildPipeline', '')}
                    suggestions={buildPipelineSuggestions}
                    isAutoComplete={true}
                    isLeadingIcon={false}
                    searchTerm={
                      typeof qa.buildPipeline === 'string'
                        ? qa.buildPipeline
                        : qa.buildPipeline && typeof qa.buildPipeline === 'object'
                          ? qa.buildPipeline.label || ''
                          : ''
                    }
                    onUpdate={async (query: string) => {
                      setLoading(true)
                      setError('')
                      try {
                        const {searchItemsBySearchField} = await import('@/services/team/teamService')
                        const results = await searchItemsBySearchField('build', query)
                        const mapped = Array.isArray(results)
                          ? results
                              .map((item: any) => ({
                                id: item.id || item._id || item.value || item.label,
                                label: getBuildTitle(item),
                                fullObject: item,
                              }))
                              .filter(option => option.label && typeof option.label === 'string')
                          : []
                        setBuildPipelineSuggestions(mapped)
                        setLoading(false)
                      } catch (err) {
                        setLoading(false)
                        setError('Failed to load suggestions')
                      }
                    }}
                    onSelectOption={(option: any) => updateQAMetric(idx, 'buildPipeline', option)}
                    onBlur={() => handleBlur(idx, 'buildPipeline')}
                  />
                  {touched[idx]?.buildPipeline && isFieldEmpty(qa.buildPipeline) && (
                    <div className="mt-1 text-xs text-red-500">Required</div>
                  )}
                  <Label className={sharedStyles.infoLabel} label="Name of the Automation test pipeline in ADO." />
                </div>
              </div>
            </form>
          </>
        </CardWrapper>
      ))}
    </>
  )
}

export default QAMetricsWidget
